import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
} from "@/src/components/ui/select";
import { useTranslation } from "@/src/i18n/client";
import { Label } from "../ui/label";

interface FileHandlingSelectProps {
    value: "single" | "multiple";
    onValueChange: (value: "single" | "multiple") => void;
    canCombine: boolean;
    hasPdfOrTiff: boolean;
    fileCount: number;
}

export function FileHandlingSelect({
    value,
    onValueChange,
    canCombine,
    hasPdfOrTiff,
    fileCount
}: FileHandlingSelectProps) {
    const { t } = useTranslation();

    // Determine display value
    const displayValue = value === "single"
        ? t('homePage.fileHandling.singlePart')
        : t('homePage.fileHandling.multipleParts');

    return (
        <div className="space-y-2">
            <div className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-4">
                <Label
                    htmlFor="file-handling-select"
                    className="block text-sm font-medium text-gray-900 dark:text-white mb-0 w-full sm:w-28"
                >
                    {t('homePage.fileHandling.label')}
                </Label>
                <div className="w-full sm:flex-1 min-w-0">
                    <Select
                        value={value}
                        onValueChange={(value) => value && onValueChange(value as "single" | "multiple")}
                    >
                        <SelectTrigger
                            id="file-handling-select"
                            className="w-full min-w-0 rounded-md border border-input bg-background px-3 py-2 text-sm text-foreground shadow-sm focus:border-primary focus:ring-2 focus:ring-primary/20 dark:bg-background dark:text-foreground dark:border-input"
                        >
                            {displayValue}
                        </SelectTrigger>
                        <SelectContent
                            className="rounded-md border border-input bg-background shadow-lg dark:bg-background dark:border-input"
                        >
                            <SelectItem
                                value="single"
                                disabled={!canCombine}
                                className="px-3 py-2 hover:bg-primary/10 dark:hover:bg-primary/20"
                            >
                                {t('homePage.fileHandling.singlePart')}
                            </SelectItem>
                            <SelectItem
                                value="multiple"
                                className="px-3 py-2 hover:bg-primary/10 dark:hover:bg-primary/20"
                            >
                                {t('homePage.fileHandling.multipleParts')}
                            </SelectItem>
                        </SelectContent>
                    </Select>
                </div>
            </div>
            {hasPdfOrTiff && fileCount > 1 && (
                <p className="text-xs text-muted-foreground pl-0 sm:pl-34">
                    {t('homePage.fileHandling.batchOnlyHint')}
                </p>
            )}
            {!hasPdfOrTiff && fileCount > 1 && value === 'single' && (
                <p className="text-xs text-muted-foreground pl-0 sm:pl-34">
                    {t('homePage.fileHandling.combineHint')}
                </p>
            )}
        </div>
    );
}
